import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const PageNotFound = () => {
  const canvasRef = useRef(null);
  const [gameOver, setGameOver] = useState(false);
  const [isWin, setIsWin] = useState(false);
  const [score, setScore] = useState(0);
  const [resetFlag, setResetFlag] = useState(false);

  useEffect((gameOver) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Game variables
    let ballX = canvas.width / 2;
    let ballY = canvas.height - 30;
    let ballDX = 2;
    let ballDY = -2;
    let paddleX = (canvas.width - 75) / 2;
    const paddleWidth = 75;
    const paddleHeight = 10;
    const paddleSpeed = 7;
    let points = 0;

    let rightPressed = false;
    let leftPressed = false;

    let bricks = [];
    const brickRowCount = 3;
    const brickColumnCount = 5;
    const brickWidth = 75;
    const brickHeight = 20;
    const brickPadding = 10;
    const brickOffsetTop = 30;
    const brickOffsetLeft = 30;

    for (let c = 0; c < brickColumnCount; c++) {
      bricks[c] = [];
      for (let r = 0; r < brickRowCount; r++) {
        bricks[c][r] = { x: 0, y: 0, status: 1 };
      }
    }

    const keyDownHandler = (e) => {
      if (e.key === 'Right' || e.key === 'ArrowRight') rightPressed = true;
      else if (e.key === 'Left' || e.key === 'ArrowLeft') leftPressed = true;
    };

    const keyUpHandler = (e) => {
      if (e.key === 'Right' || e.key === 'ArrowRight') rightPressed = false;
      else if (e.key === 'Left' || e.key === 'ArrowLeft') leftPressed = false;
    };

    document.addEventListener('keydown', keyDownHandler);
    document.addEventListener('keyup', keyUpHandler);

    function drawBall() {
      ctx.beginPath();
      ctx.arc(ballX, ballY, 10, 0, Math.PI * 2);
      ctx.fillStyle = '#22c55e';
      ctx.fill();
      ctx.closePath();
    }

    function drawPaddle() {
      ctx.beginPath();
      ctx.rect(paddleX, canvas.height - paddleHeight, paddleWidth, paddleHeight);
      ctx.fillStyle = '#4ade80';
      ctx.fill();
      ctx.closePath();
    }

    function drawBricks() {
      for (let c = 0; c < brickColumnCount; c++) {
        for (let r = 0; r < brickRowCount; r++) {
          if (bricks[c][r].status === 1) {
            let brickX = c * (brickWidth + brickPadding) + brickOffsetLeft;
            let brickY = r * (brickHeight + brickPadding) + brickOffsetTop;
            bricks[c][r].x = brickX;
            bricks[c][r].y = brickY;
            ctx.beginPath();
            ctx.rect(brickX, brickY, brickWidth, brickHeight);
            ctx.fillStyle = '#16a34a';
            ctx.fill();
            ctx.closePath();
          }
        }
      }
    }

    function checkWin() {
      for (let c = 0; c < brickColumnCount; c++) {
        for (let r = 0; r < brickRowCount; r++) {
          if (bricks[c][r].status === 1) return false;
        }
      }
      return true;
    }

    function collisionDetection() {
      for (let c = 0; c < brickColumnCount; c++) {
        for (let r = 0; r < brickRowCount; r++) {
          let b = bricks[c][r];
          if (b.status === 1) {
            if (
              ballX > b.x &&
              ballX < b.x + brickWidth &&
              ballY > b.y &&
              ballY < b.y + brickHeight
            ) {
              ballDY = -ballDY;
              b.status = 0;
              points += 10;
              setScore(points);

              if (checkWin()) {
                setGameOver(true);
                setIsWin(true);
              }
            }
          }
        }
      }
    }

    function drawScore() {
      ctx.font = '16px monospace';
      ctx.fillStyle = '#a3e635';
      ctx.fillText(`Score: ${points}`, 8, 20);
    }

    function draw() {
      if (gameOver) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawBricks();
      drawBall();
      drawPaddle();
      drawScore();
      collisionDetection();

      if (ballX + ballDX > canvas.width - 10 || ballX + ballDX < 10) ballDX = -ballDX;
      if (ballY + ballDY < 10) ballDY = -ballDY;
      else if (ballY + ballDY > canvas.height - 10) {
        if (ballX > paddleX && ballX < paddleX + paddleWidth) {
          ballDY = -ballDY;
        } else {
          setGameOver(true);
          setIsWin(false);
          return;
        }
      }

      if (rightPressed && paddleX < canvas.width - paddleWidth) {
        paddleX += paddleSpeed;
      } else if (leftPressed && paddleX > 0) {
        paddleX -= paddleSpeed;
      }

      ballX += ballDX;
      ballY += ballDY;

      requestAnimationFrame(draw);
    }

    draw();

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
      document.removeEventListener('keyup', keyUpHandler);
    };
  }, [resetFlag]);

  const handleReset = () => {
    setGameOver(false);
    setIsWin(false);
    setScore(0);
    setResetFlag((prev) => !prev); // re-trigger useEffect
  };

  return (
    <div className="min-h-screen bg-black text-lime-300 flex flex-col items-center justify-center py-10 px-4">
      <h1 className="text-4xl font-bold mb-2 text-center">
        ⚠️ 404: Page Not Found
      </h1>
      <p className="mb-6 text-center text-lime-400 max-w-xl">
        You've hit a dead pixel in the universe. While we search for the missing loot crate, enjoy this retro game!
      </p>

      <canvas
        ref={canvasRef}
        width="475"
        height="300"
        className="border-2 border-lime-500 bg-gray-900 mb-4 rounded shadow-md"
      />

      <div className="text-lime-400 text-md mb-4">Current Score: {score}</div>

      {gameOver && (
        <div className="flex flex-col items-center gap-4">
          {isWin ? (
            <div className="text-green-400 font-bold text-xl animate-pulse">
              🎉 Victory! You broke all the bricks!
            </div>
          ) : (
            <div className="text-red-500 font-bold text-xl animate-pulse">
              💀 Mission Failed
            </div>
          )}
          <button
            onClick={handleReset}
            className="bg-lime-500 hover:bg-lime-600 text-black font-semibold px-4 py-2 rounded shadow"
          >
            🔄 Try Again
          </button>
        </div>
      )}

      <Link
        to="/"
        className="mt-4 bg-lime-500 hover:bg-lime-600 text-black font-semibold px-5 py-2 rounded shadow"
      >
        ⬅️ Back to Home Base
      </Link>
    </div>
  );
};

export default PageNotFound;
